import { ChangeEvent, FormEventHandler, useCallback, useState } from "react"
import { Alert, Button, Col, Container, Form, Row } from 'react-bootstrap'
import { useTranslation } from "react-i18next";
import { useGA4Event } from '../../hooks/useGA4Event'
import { Job, location } from "../../contents/job/Job"
import { jobs } from "../../contents/job/jobs"
import { Turnstile } from '@marsidev/react-turnstile'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || ""
const isTurnstileEnabled = !!TURNSTILE_SITE_KEY

const gFormInputIds = {
  category: "689339063",
  name: "2043954811",
  kana: "257148033",
  email: "416976063",
  phone: "513308762",
  content: "1323439905"
} as const

type FormData = {
  name: string
  kana?: string
  email: string
  phone: string | undefined
  position: string
  location: typeof location[keyof typeof location]
  currentJob?: string
  github?: string
  selfPromotion?: string
  isMainJob?: boolean
}

type ApplicationFormProps = {
  job?: Job
}

const ApplicationForm = ({ job = jobs[0] }: ApplicationFormProps) => {
  const { t } = useTranslation("jobs")
  const { t: tTrans } = useTranslation("translations", { keyPrefix: "EMPLOYMENT.APPLICATION_FORM" })
  const { sendEvent } = useGA4Event()
  const defaultFormData: FormData = {
    name: "",
    kana: "",
    email: "",
    phone: "",
    position: job.title,
    location: location[job.location],
    currentJob: "",
    github: "",
    selfPromotion: "",
    isMainJob: false
  }
  const [formData, setFormData] = useState<FormData>(defaultFormData)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [sending, setSending] = useState<boolean>(false)
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)

  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token)
  }, [])

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null)
  }, [])

  const handleValueChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    if (errorMessage !== null) setErrorMessage(null)

    const fieldName = event.target.getAttribute("name") as keyof FormData
    const value = event.currentTarget.value

    setFormData((data) => ({...data, [fieldName]: value}))
  }

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((data) => ({
      ...data,
      isMainJob: event.target.checked
    }))
  }

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (isTurnstileEnabled && !turnstileToken) {
      return
    }

    setSending(true)

    const content = `
【希望ポジション】${formData.position}
【希望勤務形態】${formData.location}
【現在の職業】${formData.currentJob}
【GitHubリンク】${formData.github}
【自己アピール】${formData.selfPromotion}
  `.trim()

    const entries = {
      [`entry.${gFormInputIds.category}`]: "採用",
      [`entry.${gFormInputIds.name}`]: formData.name,
      [`entry.${gFormInputIds.kana}`]: formData.kana ?? "",
      [`entry.${gFormInputIds.email}`]: formData.email,
      [`entry.${gFormInputIds.phone}`]: formData.phone ?? "",
      [`entry.${gFormInputIds.content}`]: content,
    }

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...entries,
          "cf-turnstile-response": turnstileToken,
        }),
      })

      if (!res.ok) {
        throw new Error("submission failed")
      }

      sendEvent('form_submit', {
        form_type: 'application',
        position: formData.position,
        location: formData.location,
        has_github: !!formData.github,
      })

      window.location.href = "/employment/application-submitted"
    } catch (e) {
      setSending(false)
      setErrorMessage(tTrans("ALERT_ERROR"))
    }
  }

  const getTranslatedPosition = (positionKey: string) => t(positionKey)
  const getTranslatedWorkStyle = (style: string) => {
    const mapping: Record<string, string> = {
      "リモートワーク": tTrans("WORK_STYLE_TELECOMMUTE"),
      "オフィスワーク": tTrans("WORK_STYLE_COMMUTE"),
      "どちらでも": tTrans("WORK_STYLE_EITHER")
    }
    return mapping[style] || style
  }

  return (
    <div id="contact" style={{backgroundColor: "lightgray"}}>
      <Container>
        <article className="py-5">
          <div className="text-center mb-4">
            <h1 className="mb-0">{tTrans("TITLE")}</h1>
            <small>{tTrans("SUBTITLE")}</small>
          </div>
          <Form onSubmit={handleSubmit} className="mx-auto mt-4">
            <input type="hidden" name="form-name" value="contact" />
            <Row className="mx-auto">
              <Col xl={{ span: 6, offset: 3 }}>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between">
                    <p className="mb-0">{tTrans("LABEL_NAME")}</p>
                    <small className="text-end bg-dark text-light py-1 px-2">{tTrans("LABEL_REQUIRED")}</small>
                  </Form.Label>
                  <Form.Control
                    name="name"
                    value={formData.name}
                    onChange={handleValueChange}
                    type="text"
                    className="border-0 rounded-0"
                    style={{backgroundColor: "rgb(241, 242, 246)"}}
                    required
                    disabled={sending}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between">
                    <p className="mb-0">{tTrans("LABEL_KANA")}</p>
                    <small className="text-end bg-dark text-light py-1 px-2">{tTrans("LABEL_REQUIRED")}</small>
                  </Form.Label>
                  <Form.Control
                    name="kana"
                    value={formData.kana}
                    onChange={handleValueChange}
                    type="text"
                    className="border-0 rounded-0"
                    style={{backgroundColor: "rgb(241, 242, 246)"}}
                    required
                    disabled={sending}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between"><p className="mb-0">{tTrans("LABEL_EMAIL")}</p><small className="text-end bg-dark text-light py-1 px-2">{tTrans("LABEL_REQUIRED")}</small></Form.Label>
                  <Form.Control name="email" value={formData.email} onChange={handleValueChange} type="email" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} required disabled={sending} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>{tTrans("LABEL_PHONE")}</Form.Label>
                  <Form.Control name="phone" value={formData.phone} onChange={handleValueChange} type="tel" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} disabled={sending} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between"><p className="mb-0">{tTrans("LABEL_POSITION")}</p><small className="text-end bg-dark text-light py-1 px-2">{tTrans("LABEL_REQUIRED")}</small></Form.Label>
                  <Form.Select name="position" value={formData.position} onChange={handleValueChange} as="select" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} required disabled={sending}>
                    {jobs.map((j) => (
                      <option key={j.slug} value={j.title}>{getTranslatedPosition(j.title)}</option>
                    ))}
                    <option value="その他">{tTrans("CATEGORY_OTHERS")}</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between"><p className="mb-0">{tTrans("LABEL_WORK_STYLE")}</p><small className="text-end bg-dark text-light py-1 px-2">{tTrans("LABEL_REQUIRED")}</small></Form.Label>
                  <Form.Select name="location" value={formData.location} onChange={handleValueChange} as="select" className="border-0 rounded-0" style={{backgroundColor: "rgb(241, 242, 246)"}} required disabled={sending}>
                    {Object.entries(location).map(([k, v]) => (
                      <option key={k} value={v}>{getTranslatedWorkStyle(v)}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between">
                    <p className="mb-0">{tTrans("LABEL_CURRENT_JOB")}</p>
                    <small className="text-end bg-dark text-light py-1 px-2">{tTrans("LABEL_REQUIRED")}</small>
                  </Form.Label>
                  <Form.Control
                    name="currentJob"
                    value={formData.currentJob}
                    onChange={handleValueChange}
                    type="text"
                    className="border-0 rounded-0"
                    style={{backgroundColor: "rgb(241, 242, 246)"}}
                    required
                    disabled={sending}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between">
                    <p className="mb-0">{tTrans("LABEL_GITHUB")}</p>
                    <small className="text-end bg-dark text-light py-1 px-2">{tTrans("LABEL_REQUIRED")}</small>
                  </Form.Label>
                  <Form.Control
                    name="github"
                    value={formData.github}
                    onChange={handleValueChange}
                    type="url"
                    placeholder="https://github.com/yourname"
                    className="border-0 rounded-0"
                    style={{backgroundColor: "rgb(241, 242, 246)"}}
                    required
                    disabled={sending}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="d-flex justify-content-between">
                    <p className="mb-0" style={{ marginTop: 'auto', marginBottom: 'auto' }}>{tTrans("LABEL_SELF_PROMOTION")}</p>
                    <small className="text-right bg-secondary text-light py-1 px-2" style={{ marginTop: 'auto', marginBottom: 'auto' }}>{tTrans("LABEL_OPTIONAL")}</small>
                  </Form.Label>
                  <Form.Control
                    name="selfPromotion"
                    value={formData.selfPromotion}
                    onChange={handleValueChange}
                    as="textarea"
                    rows={4}
                    className="border-0 rounded-0"
                    style={{backgroundColor: "rgb(241, 242, 246)"}}
                    disabled={sending}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    id="isMainJob"
                    name="isMainJob"
                    label={tTrans("CHECKBOX_MAIN_JOB")}
                    checked={formData.isMainJob || false}
                    onChange={handleCheckboxChange}
                    required
                    disabled={sending}
                  />
                </Form.Group>
                {isTurnstileEnabled && (
                  <div className="mb-3">
                    <Turnstile siteKey={TURNSTILE_SITE_KEY} onSuccess={handleTurnstileVerify} onExpire={handleTurnstileExpire} />
                  </div>
                )}
                <Button
                  variant="success"
                  className="w-100 border-0 rounded-0 text-light mt-2"
                  type="submit"
                  disabled={sending || !formData.isMainJob || (isTurnstileEnabled && !turnstileToken)}
                >
                  {sending ? tTrans("BUTTON_SENDING") : tTrans("BUTTON_SUBMIT")}
                </Button>
                {errorMessage !== null && (
                  <Alert variant="danger" className="mt-2">
                    {errorMessage}
                  </Alert>
                )}
              </Col>
            </Row>
          </Form>
        </article>
      </Container>
    </div>
  )
}

export { ApplicationForm }
